import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ShieldCheck, QrCode, ArrowUpRight, Leaf, ScanLine, CheckCircle2, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { POP } from '@/constants/testIds';
import { useI18n } from '@/i18n/I18nProvider';

// Real, high-quality product imagery (Klymart Verified Impact bottle).
// Crops the right portion of the deck "problem" slide via brand-img-right helper.
const PRODUCT_IMAGE = '/brand/problem.png';

// ---------- Decorative QR code (deterministic SVG, not a real encoder) ----------
function QrCodePattern({ size = 200, color = '#0a0e14', bg = '#fff' }) {
  const cells = 25;
  const cellSize = size / cells;
  const pattern = useMemo(() => buildQrPattern(cells), []);
  const radius = cellSize * 0.22;

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label="Klymart verification QR"
      className="rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.45)]"
    >
      <rect width={size} height={size} fill={bg} rx={size * 0.06} />
      {pattern.map((p) => (
        <rect
          key={`${p.r}-${p.c}`}
          x={p.c * cellSize + cellSize * 0.08}
          y={p.r * cellSize + cellSize * 0.08}
          width={cellSize * 0.84}
          height={cellSize * 0.84}
          fill={color}
          rx={radius}
        />
      ))}
      {/* Centered Klymart leaf mark */}
      <g transform={`translate(${size / 2 - 14}, ${size / 2 - 14})`}>
        <rect width={28} height={28} rx={6} fill="#0a0e14" />
        <path
          d="M9 19c0-6 4-10 10-10 0 6-4 10-10 10z"
          fill="hsl(173, 72%, 60%)"
        />
        <path
          d="M9 19c2-4 5-7 10-10"
          stroke="#0a0e14"
          strokeWidth="1.2"
          fill="none"
        />
      </g>
    </svg>
  );
}

function buildQrPattern(cells) {
  const out = [];
  // 3 finder patterns (TL, TR, BL)
  const corners = [
    [0, 0],
    [0, cells - 7],
    [cells - 7, 0],
  ];
  for (const [orr, oc] of corners) {
    for (let r = 0; r < 7; r++) {
      for (let c = 0; c < 7; c++) {
        const isOuter = r === 0 || r === 6 || c === 0 || c === 6;
        const isInner = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        if (isOuter || isInner) out.push({ r: orr + r, c: oc + c });
      }
    }
  }
  // Timing tracks (faint pseudo-random) – avoid the finder zones
  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      const inTL = r < 8 && c < 8;
      const inTR = r < 8 && c >= cells - 8;
      const inBL = r >= cells - 8 && c < 8;
      if (inTL || inTR || inBL) continue;
      // Reserve area near center for the logo
      const dx = r - cells / 2;
      const dy = c - cells / 2;
      if (Math.abs(dx) <= 3 && Math.abs(dy) <= 3) continue;
      // Deterministic noise
      const v = (r * 73856093) ^ (c * 19349663);
      if (((v >>> 0) % 7) < 3) out.push({ r, c });
    }
  }
  return out;
}

// ---------- Animated character scramble (for "Verifying…") ----------
function CryptoTicker({ active }) {
  const [text, setText] = useState('0xv3rda…f7ab');
  useEffect(() => {
    if (!active) return;
    const chars = '0123456789abcdef';
    let raf;
    const tick = () => {
      let s = '0x';
      for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
      s += '…';
      for (let i = 0; i < 4; i++) s += chars[Math.floor(Math.random() * chars.length)];
      setText(s);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return <span className="font-mono text-[10px] tracking-wider">{text}</span>;
}

// ---------- Main component ----------
export default function PointOfPurchase() {
  const { m } = useI18n();
  const p = m.pop;

  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });

  // States: idle -> scanning -> verifying -> verified
  const [phase, setPhase] = useState('idle');
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const run = useCallback(() => {
    clearTimers();
    setPhase('scanning');
    timersRef.current.push(setTimeout(() => setPhase('verifying'), 1400));
    timersRef.current.push(setTimeout(() => setPhase('verified'), 2100));
  }, []);

  // Auto-play once on first scroll-into-view
  useEffect(() => {
    if (inView && phase === 'idle') {
      const t = setTimeout(run, 400);
      timersRef.current.push(t);
    }
    return clearTimers;
    // `clearTimers`, `phase`, and `run` are intentionally excluded:
    //   - clearTimers and run are stable callbacks; including them would re-trigger
    //     the auto-play unnecessarily.
    //   - phase is checked once on inView to gate the very first auto-play only.
  }, [inView]);

  // Clean up any pending timers on unmount
  useEffect(() => clearTimers, []);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <section
      id="point-of-purchase"
      data-testid={POP.root}
      ref={sectionRef}
      className="relative section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] items-center">
          {/* LEFT: copy + surfaces */}
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {p.eyebrow}
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-[44px] tracking-[-0.015em] leading-[1.05]">
              {p.heading}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {p.sub}
            </p>

            <div className="mt-7 grid grid-cols-2 gap-2 max-w-md">
              {p.surfaces.map((s, i) => (
                <div
                  key={s}
                  data-testid={`${POP.surfacesPrefix}-${i}`}
                  className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs text-foreground/85"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  {s}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: interactive product trust layer card */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <Card className="relative overflow-hidden bg-card border border-border rounded-2xl p-5 sm:p-6 glow-ring">
              <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(500px circle at 100% 0%, hsla(173,72%,40%,0.16), transparent 55%), radial-gradient(420px circle at 0% 100%, hsla(156,45%,34%,0.12), transparent 60%)',
                }}
              />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Product trust layer
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={phase === 'verified' ? 'verified' : 'pending'}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.25 }}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider ${
                        phase === 'verified'
                          ? 'border-accent/50 bg-accent/15 text-accent'
                          : 'border-border bg-secondary text-muted-foreground'
                      }`}
                    >
                      {phase === 'verified' ? (
                        <>
                          <CheckCircle2 className="h-3 w-3" />
                          {p.product.verified}
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="h-3 w-3" />
                          {p.product.verifyChip}
                        </>
                      )}
                    </motion.span>
                  </AnimatePresence>
                </div>

                {/* Product + QR demo */}
                <div className="mt-4 grid grid-cols-[140px_1fr] gap-4 items-center">
                  <button
                    type="button"
                    onClick={run}
                    aria-label={p.product.scanCta}
                    data-testid="pop-product-scan"
                    className="group relative h-36 w-36 rounded-xl border border-border overflow-hidden bg-secondary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {/* Product image */}
                    {!imgError ? (
                      <>
                        {!imgLoaded && (
                          <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[hsla(173,72%,40%,0.10)] to-[hsla(156,45%,34%,0.10)]" />
                        )}
                        <img
                          src={PRODUCT_IMAGE}
                          alt={p.product.name}
                          loading="lazy"
                          onLoad={() => setImgLoaded(true)}
                          onError={() => setImgError(true)}
                          style={{ objectPosition: '78% center' }}
                          className={`absolute inset-0 h-full w-full object-cover transition duration-700 ${
                            imgLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                          } group-hover:scale-[1.04]`}
                        />
                        <div
                          aria-hidden
                          className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"
                        />
                      </>
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[hsla(173,72%,40%,0.18)] to-[hsla(156,45%,34%,0.18)] flex items-center justify-center">
                        <Leaf className="h-12 w-12 text-primary/70" />
                      </div>
                    )}

                    {/* QR overlay (visible during scanning/verifying/verified) */}
                    <AnimatePresence>
                      {phase !== 'idle' && (
                        <motion.div
                          key="qr-overlay"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.35 }}
                          className="absolute inset-0 bg-black/55 backdrop-blur-[2px] flex items-center justify-center"
                        >
                          <div className="relative">
                            <QrCodePattern size={104} />
                            {/* Scan line */}
                            {(phase === 'scanning' || phase === 'verifying') && (
                              <motion.div
                                initial={{ y: 0 }}
                                animate={{ y: [0, 96, 0] }}
                                transition={{
                                  duration: 1.1,
                                  repeat: phase === 'scanning' ? Infinity : 0,
                                  ease: 'easeInOut',
                                }}
                                className="absolute left-1 right-1 h-[3px] rounded-full"
                                style={{
                                  background:
                                    'linear-gradient(90deg, transparent, hsla(173,72%,55%,0.95), transparent)',
                                  boxShadow: '0 0 18px 2px hsla(173,72%,55%,0.7)',
                                }}
                              />
                            )}
                            {/* Verified tick */}
                            <AnimatePresence>
                              {phase === 'verified' && (
                                <motion.div
                                  initial={{ scale: 0.6, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                                  className="absolute inset-0 rounded-xl flex items-center justify-center"
                                >
                                  <span className="h-12 w-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-[0_0_30px_rgba(46,160,107,0.7)]">
                                    <CheckCircle2 className="h-7 w-7" strokeWidth={2.5} />
                                  </span>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>

                  {/* Right side info */}
                  <div className="min-w-0">
                    <div className="font-display text-lg leading-tight">{p.product.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.product.meta}</div>

                    <div className="mt-3 h-6 flex items-center text-xs">
                      <AnimatePresence mode="wait">
                        {phase === 'idle' && (
                          <motion.span
                            key="idle"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-muted-foreground"
                          >
                            {p.product.tapHint}
                          </motion.span>
                        )}
                        {phase === 'scanning' && (
                          <motion.span
                            key="scan"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="inline-flex items-center gap-1.5 text-primary"
                          >
                            <ScanLine className="h-3.5 w-3.5" />
                            {p.product.scanning}
                          </motion.span>
                        )}
                        {phase === 'verifying' && (
                          <motion.span
                            key="verify"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="inline-flex items-center gap-2 text-primary"
                          >
                            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                            {p.product.verifying}
                          </motion.span>
                        )}
                        {phase === 'verified' && (
                          <motion.span
                            key="done"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="inline-flex items-center gap-1.5 text-accent"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {p.product.verifiedHint}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>

                    <Button
                      data-testid={POP.scanCta}
                      variant="secondary"
                      onClick={run}
                      disabled={phase === 'scanning' || phase === 'verifying'}
                      className="mt-3 h-9 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border disabled:opacity-70"
                    >
                      {phase === 'verified' ? (
                        <>
                          <RotateCw className="h-4 w-4 mr-1.5" />
                          {p.product.rescan}
                        </>
                      ) : (
                        <>
                          <QrCode className="h-4 w-4 mr-1.5" />
                          {p.product.scanCta}
                          <ArrowUpRight className="h-3.5 w-3.5 ml-1.5" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="mt-5 h-px bg-border" />

                <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                  {p.product.checks.map((c, i) => {
                    const reveal = phase === 'verified';
                    return (
                      <motion.li
                        key={c.label}
                        initial={false}
                        animate={
                          reveal
                            ? {
                                borderColor: 'hsla(156, 45%, 50%, 0.45)',
                                boxShadow: '0 0 0 1px hsla(156,45%,50%,0.18)',
                              }
                            : {
                                borderColor: 'hsla(215, 22%, 18%, 1)',
                                boxShadow: '0 0 0 0 hsla(0,0%,0%,0)',
                              }
                        }
                        transition={{ duration: 0.5, delay: reveal ? 0.05 + i * 0.08 : 0 }}
                        className="rounded-lg border bg-secondary/30 px-3 py-2.5"
                      >
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                          <AnimatePresence>
                            {reveal && (
                              <motion.span
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ delay: 0.05 + i * 0.08, type: 'spring', stiffness: 360, damping: 16 }}
                                className="h-3 w-3 rounded-full bg-accent/20 border border-accent/50 inline-flex items-center justify-center shrink-0"
                              >
                                <CheckCircle2 className="h-2 w-2 text-accent" strokeWidth={3} />
                              </motion.span>
                            )}
                          </AnimatePresence>
                          <span className="truncate">{c.label}</span>
                        </div>
                        <div className="mt-1 font-display text-sm leading-tight text-foreground">
                          {c.value}
                        </div>
                      </motion.li>
                    );
                  })}
                </ul>

                <div className="mt-4 flex items-center justify-between text-[10px] text-muted-foreground">
                  <CryptoTicker active={phase === 'verifying'} />
                  <span>cryptographically signed</span>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
